import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Kanban,
  MapTrifold,
  Stack,
  Gear,
  User,
  UserCircle,
  X,
  ChartLine,
  ChartBarHorizontal,
  Ruler,
  Cube,
} from '@phosphor-icons/react';
import { useAppStore } from '@/store/appStore';
import { useCatalogs } from '@/hooks/useTasks';
import { getCurrentUserEmail } from '@/auth/getCurrentUser';

type NavItem = {
  icon: React.ReactNode;
  label: string;
  path: string;
  // Predicado opcional para marcar activo en rutas distintas a `path`
  // (ej. "Tareas" abarca kanban/tabla/planificador/calendario).
  matches?: (pathname: string) => boolean;
};

// Lista plana de destinos del drawer izquierdo. Las 4 vistas de tareas
// (kanban en sus 3 agrupaciones + tabla + planificador + calendario) se
// unificaron bajo "Tareas"; el tipo de vista y la agrupación se eligen en la
// ViewToolbar. Quedan: resumen (Dashboard), tareas, lotes (Sitio/Trámites),
// config.
const NAV_ITEMS: NavItem[] = [
  { icon: <ChartLine size={18} weight="bold" />,  label: 'Dashboard',     path: '/' },
  {
    icon: <Kanban size={18} weight="bold" />,
    label: 'Tareas',
    path: '/kanban/status',
    matches: (p) => p.startsWith('/kanban/') || p === '/table' || p === '/planner' || p === '/calendar',
  },
  { icon: <ChartBarHorizontal size={18} weight="bold" />, label: 'Gantt', path: '/gantt' },
  { icon: <MapTrifold size={18} weight="bold" />, label: 'Sitio',         path: '/site-plan' },
  { icon: <Stack size={18} weight="bold" />,      label: 'Trámites',      path: '/tramites' },
  { icon: <Ruler size={18} weight="bold" />,      label: 'Planta',        path: '/planta' },
  // Exploración aparte de Planta, a propósito: otro motor (tiles + marcas) y
  // otras tablas. Si se descarta, se borra esta línea.
  { icon: <Stack size={18} weight="bold" />,      label: 'Láminas',       path: '/laminas' },
  // Inventario de formaleta de aluminio. Vive acá mientras se construye y
  // después se independiza: si se muda, se borra esta línea.
  { icon: <Cube size={18} weight="bold" />,       label: 'Formaleta',     path: '/formaleta' },
  { icon: <Gear size={18} weight="bold" />,       label: 'Configuración', path: '/configuracion' },
  // TEMPORAL (29-oct-2026). El hueco de 62 pt bajo la barra no se reproduce
  // en Chromium —pasa solo en modo app de iOS— así que la única forma de
  // medirlo es desde el aparato. Se borra junto con la página.
  { icon: <Gear size={18} weight="bold" />,       label: 'Diagnóstico',   path: '/diagnostico' },
];

export function NavMenu() {
  const open = useAppStore((s) => s.navMenuOpen);
  const setOpen = useAppStore((s) => s.setNavMenuOpen);
  const navigate = useNavigate();
  const location = useLocation();
  const userEmail = getCurrentUserEmail();
  const { data: catalogs } = useCatalogs();

  // Resolver el IDCol del usuario actual (por email) para el item "Mis tareas".
  const me = catalogs?.users.find(
    (u) => u.correo && userEmail && u.correo.toLowerCase() === userEmail.toLowerCase(),
  );
  const items: NavItem[] = [
    NAV_ITEMS[0], // Dashboard
    ...(me
      ? [{
          icon: <UserCircle size={18} weight="bold" />,
          label: 'Mis tareas',
          path: `/personal/${me.idCol}`,
          matches: (p: string) => p.startsWith('/personal/'),
        } as NavItem]
      : []),
    ...NAV_ITEMS.slice(1),
  ];

  // Cierra con ESC (sin importar donde este el foco).
  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, setOpen]);

  function go(path: string) {
    navigate(path);
    setOpen(false);
  }

  return (
    <>
      {/* Backdrop entre las dos franjas, para no taparlas. Los altos ya no se
          repiten a mano acá: `--alto-arriba` y `--alto-abajo` (styles/index.css)
          incluyen el recorte del teléfono, que cambia de aparato en aparato. */}
      {open && (
        <div
          onClick={() => setOpen(false)}
          className="fixed left-0 right-0 top-[var(--alto-arriba)] bottom-[var(--alto-abajo)] z-40 bg-black/20 transition-opacity"
        />
      )}

      <aside
        className={`fixed left-0 top-[var(--alto-arriba)] bottom-[var(--alto-abajo)] z-50 w-[260px] bg-white border-r border-stone-200 shadow-xl transform transition-transform duration-200 ${
          open ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        {/* Header: avatar + email + cerrar */}
        <div className="flex items-center justify-between gap-2 border-b border-stone-200 px-5 py-4">
          <div className="flex min-w-0 items-center gap-2">
            <User size={18} weight="bold" className="shrink-0 text-brand-gray-400" />
            <span
              className="truncate text-sm font-medium text-brand-gray-500"
              title={userEmail ?? undefined}
            >
              {userEmail ?? 'Sin sesión'}
            </span>
          </div>
          <button
            onClick={() => setOpen(false)}
            className="shrink-0 rounded p-1 text-brand-gray-400 transition-colors hover:bg-stone-100 hover:text-brand-gray-500"
            aria-label="Cerrar menú"
          >
            <X size={18} weight="bold" />
          </button>
        </div>

        {/* Items */}
        <nav className="flex flex-col gap-1 px-3 py-3">
          {items.map((it) => {
            // Activo si la ruta coincide exactamente, o si el item define un
            // predicado `matches` que abarca varias rutas (ej. "Tareas").
            const isActive = it.matches
              ? it.matches(location.pathname)
              : location.pathname === it.path;
            return (
              <button
                key={it.path}
                type="button"
                onClick={() => go(it.path)}
                className={`flex items-center gap-2.5 rounded px-3 py-2 text-sm transition-colors ${
                  isActive
                    ? 'bg-brand-green-100 text-black font-semibold'
                    : 'text-brand-gray-500 hover:bg-stone-100'
                }`}
              >
                {it.icon}
                {it.label}
              </button>
            );
          })}
        </nav>
      </aside>
    </>
  );
}
